import type {
  TextFieldClasses as MuitextFieldClasses,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';

import { useFormContext, Controller } from 'react-hook-form';
import { useValidationTranslation } from '@mfw/components';
import MuiTextField from '@mui/material/TextField';
import classNames from 'classnames';
import React from 'react';

import type { BaseFieldType } from './types';

import TextFieldSkeleton from '../TextFieldSkeleton';

// ----------------------------------------------------------------------

export type TextFieldProps = { 'data-testid'?: string } & MuiTextFieldProps &
  BaseFieldType;

const TextField = ({
  className,
  'data-testid': testId,
  disabled,
  endAdornment,
  helperText,
  isLoading,
  name,
  readonly,
  required = false,
  startAdornment,
  type,
  ...other
}: TextFieldProps) => {
  const { control } = useFormContext();

  const { translate } = useValidationTranslation();

  const classes = React.useMemo(() => {
    return {
      root: classNames('Mfw-TextField-root'),
    } as MuitextFieldClasses;
  }, []);
  return (
    <Controller
      render={({ field, fieldState: { error } }) => {
        if (isLoading) {
          return <TextFieldSkeleton />;
        }
        return (
          <MuiTextField
            {...field}
            InputProps={{
              endAdornment: endAdornment && (
                <div className="pl-2 flex justify-center items-center">
                  {endAdornment}
                </div>
              ),
              readOnly: readonly,
              startAdornment: startAdornment && (
                <div className="pr-2 flex justify-center items-center">
                  {startAdornment}
                </div>
              ),
              ...other?.InputProps,
            }}
            // a legacy numeric 0 default shows as an empty field; the string
            // '0' is a legitimate first character — local phone numbers are
            // written 06x, and blanking it swallowed the leading digit
            value={field.value === 0 ? '' : field.value}
            onChange={(event) => {
              field.onChange(event.target.value);
            }}
            className={classNames('autofill:bg-transparent', className)}
            helperText={error ? translate(error?.message) : helperText}
            aria-readonly={readonly}
            disabled={disabled}
            required={required}
            classes={classes}
            error={!!error}
            type={type}
            fullWidth
            {...other}
            inputProps={{ ...other?.inputProps, 'data-testid': testId }}
            label={required ? `${other.label} *` : other.label}
          />
        );
      }}
      control={control}
      name={name}
    />
  );
};

export default React.memo(TextField);
